import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { api } from "@/domains/shared/services/api";
import { Roles } from "@/domains/shared/models/roles";
import { useAuth } from "@/domains/shared/hooks/useAuth";
import { initConnectivityListeners, setOfflineEnabled } from "@/domains/offline/connectivity/connectivityStore";
import { isOfflineMobileAccess } from "@/domains/offline/connectivity/deviceEligibility";
import { flushChatOutbox } from "@/domains/offline/chat/chatSyncEngine";
import { flushOutbox, pullOfflineScope } from "@/domains/offline/sync/syncEngine";
import { OUTBOX_QUEUED_EVENT } from "@/domains/offline/sync/syncEvents";
import { clearOfflineData } from "@/domains/offline/db/mivoDb";
import { getTranslation } from "@mivo/i18n";
import { OFFLINE_TRANSLATIONS } from "@/domains/offline/constants";
import { toast } from "sonner";
import { needsEmailVerification } from "@/domains/shared/services/emailVerification";
import { isClinicalAccessBlocked } from "@/domains/subscription/accountAccess";

export function OfflineProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [offlineActive, setOfflineActive] = useState(isOfflineMobileAccess);

  useEffect(() => {
    const updateOfflineActive = () => {
      const next = isOfflineMobileAccess();
      setOfflineActive(next);
      setOfflineEnabled(next);
    };

    updateOfflineActive();
    window.addEventListener("resize", updateOfflineActive);

    return () => {
      window.removeEventListener("resize", updateOfflineActive);
    };
  }, []);

  useEffect(() => initConnectivityListeners(), []);

  useEffect(() => {
    if (!offlineActive) {
      return;
    }

    const onOnline = () => {
      void flushOutbox(api).then(() => flushChatOutbox());
    };
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  }, [offlineActive]);

  useEffect(() => {
    if (!offlineActive) {
      return;
    }

    const interval = window.setInterval(() => {
      if (!navigator.onLine) {
return;
}
      void flushChatOutbox();
    }, 60_000);
    return () => window.clearInterval(interval);
  }, [offlineActive]);

  useEffect(() => {
    if (!offlineActive) {
      return;
    }

    const onQueued = () => {
      toast.message(getTranslation(OFFLINE_TRANSLATIONS.savedLocallyTitle), {
        description: getTranslation(OFFLINE_TRANSLATIONS.savedLocallySyncDescription),
      });
    };
    window.addEventListener(OUTBOX_QUEUED_EVENT, onQueued);
    return () => window.removeEventListener(OUTBOX_QUEUED_EVENT, onQueued);
  }, [offlineActive]);

  useEffect(() => {
    if (offlineActive) {
      return;
    }

    void clearOfflineData();
  }, [offlineActive]);

  useEffect(() => {
    if (!offlineActive || !user) {
return;
}
    if (needsEmailVerification(user) || isClinicalAccessBlocked(user)) {
return;
}
    const role = user.role === Roles.Admin ? "admin" : user.role;
    if (role !== Roles.Therapist && role !== Roles.Parent && role !== "admin") {
return;
}
    void pullOfflineScope(api, role).catch(() => {
      /* prefetch is best-effort */
    });
  }, [offlineActive, user]);

  return children;
}
